// React Components
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

// Material UI Components
import CircleRoundedIcon from '@mui/icons-material/CircleRounded';
import Link from '@mui/material/Link';
import StorageIcon from '@mui/icons-material/Storage';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import Avatar from '@mui/material/Avatar';
import { green, orange, red } from '@mui/material/colors';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

// Audit Vault Components
import ServiceIsDown from '../components/common/ServiceIsDown';
import ProgressBar from '../components/common/ProgressBar';
import { AUDIT_VAULT_TENANT_SOFTLIMIT_ON_DBSIZE_WARNPERCENTAGE_1_INFORM, AUDIT_VAULT_TENANT_SOFTLIMIT_ON_DBSIZE_WARNPERCENTAGE_2_STRONGWARN, AUDIT_VAULT_TENANT_SOFTLIMIT_ON_DBSIZE_WARNPERCENTAGE_3_CRITICAL, AUDIT_VAULT_TENANT_DBUNITSPURCHASED_GB_PER_UNIT } from "../constants/constants-admindatabasewarnings";


// Audit Vault Utilities
import { SUPPORT_EMAIL_TO_RECEIVE_SUPPORT_REQUESTS } from '../constants/constants-email';
import { getTenantDBStats } from '../utilities/common-healthstats';

// In this component we will display a nice simple summary of a Tenants DB Stats.
// This component is self-enclosed, you just call it from the HomePage or CompanyDashboardPage.
// Can be called with property viewFullSize = true or false.  If true it will display the full size status, if false will display a mini status for the admin pages etc.
// Optional param of showLinkInFullSize, if set to true will display the report link when displaying full size, otherwise it won't be displayed if false or not passed in.
// Optional param showTenantUserCount, if set to true will load Tenant User Count from MS Graph and display the user count (now this may not be all licensed users obviously, but it's the users that exist in AD).

// FUTURE TODO: REPLACE THE CONTACT US LINK WITH A LINK TO THE CORRECT BILLING PAGE TO ADD MORE STORAGE.

function TenantDBStats(props) {
    
    // Component Constants    
    const { user, tenant, viewFullSize, showLink, showTenantUserCount } = props;
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);    
    const [serviceIsDownError, setServiceIsDownError] = useState(false);    
    const [tenantDbStats, setTenantDbStats] = useState("");

    const containerSx = {
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowWrap: 'anywhere',
    };

    const detailsSx = {
        fontSize: '0.8rem',
        width: '100%',
        maxWidth: '100%',
        pb: 1.5,
    };

    const progressSx = {
        width: '100%',
        minWidth: 0,
        height: '10px',
        borderRadius: '5px',
    };

    const statusRowSx = {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.75,
        flexWrap: 'wrap',
        minWidth: 0,
    };

    const sectionHeaderSx = {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        minWidth: 0,
    };
 

    // Component Functions
    useEffect(() => {
        async function fetchData() {            
            try {                                                      
            
                // Now load the messages.                                                        
                const response = await getTenantDBStats(user, tenant.id, showTenantUserCount);
                if (response)
                {
                    setTenantDbStats(response);
                };      
                //console.log(response);
            }   
            catch(e) {            
                // Call function to parse the Axios Error, if 'Service Is Down' it will set the hook to true.  It also updates the setLoading and setSuccess to false.
                console.log("ERROR: TenantDBStats.fetchData");
                console.log(e);
                setServiceIsDownError(true);
            }
            finally{
                setLoading(false);  
            }                            
        }
        fetchData();
    }, []);


    // Component UI
    
    // If we are still loading data, display that in the UI...
    if (loading) {        
        return (<ProgressBar message="Loading ..." loading={loading} />);
    }

    // Add check to see if WebAPI service is running or not.
    if (serviceIsDownError) {
        return (<><ServiceIsDown></ServiceIsDown></>);
    }


    const canViewUsageReports =
        showLink &&
        user &&
        (user.isSystemAdmin || user.isCompanyAdmin || user.isTenantAdmin || user.isTenantReportReader);

    const renderReportsButton = () => canViewUsageReports && (
        <Box sx={{ mt: 2 }}>
            <Button size="small" variant="outlined" onClick={() => navigate(`/Reports/ReportsHome`)}>See Usage Reports</Button>
        </Box>
    );

    const renderJobHealth = () => {
        const hasErrors = tenantDbStats.totalNumberOfErrors > 0;
        return (
            <>
                <u>Job Health:</u>
                <br />
                <CircleRoundedIcon sx={{ color: hasErrors ? red[500] : green[500], height: 15, width: 15 }} />
                &nbsp;{tenantDbStats.totalNumberOfErrors} Job Errors.
                <br />
            </>
        );
    };

    const renderTenantHeading = () => (
        <>Tenant Id: {tenant.id} <br />
            Domain: <b>{tenant.tenantUrl}</b><br />
            User Count: {tenantDbStats.totalNumberOfUsers}
        </>
    );

    const getHostedStorageStatus = (usagePercent) => {
        if (usagePercent >= AUDIT_VAULT_TENANT_SOFTLIMIT_ON_DBSIZE_WARNPERCENTAGE_3_CRITICAL) {
            return {
                level: 3,
                color: red[500],
                textColor: 'red',
                message: "Your storage space usage is at a critical level, please contact Support to increase your available storage space immediately to avoid running out of space.",
            };
        }

        if (usagePercent >= AUDIT_VAULT_TENANT_SOFTLIMIT_ON_DBSIZE_WARNPERCENTAGE_2_STRONGWARN) {
            return {
                level: 2,
                color: red[500],
                textColor: 'red',
                message: "Your storage space remaining is nearing capacity, we strongly recommend you contact Support to increase your available storage space.",
            };
        }

        if (usagePercent >= AUDIT_VAULT_TENANT_SOFTLIMIT_ON_DBSIZE_WARNPERCENTAGE_1_INFORM) {
            return {
                level: 1,
                color: orange[500],
                textColor: 'orange',
                message: "Your storage space remaining is nearing our first warning level, we recommend you proactively start thinking about increasing your available storage space or contacting us at Support.",
            };
        }

        return {
            level: 0,
            color: green[500],
            textColor: 'green',
            message: "You have plenty of storage for now.",
        };
    };

    const renderHostedStatusIcon = (status, variant = "avatar") => {
        if (variant === "circle") {
            return (
                <Tooltip title={status.message} arrow>
                    <CircleRoundedIcon sx={{ color: status.color, height: 15, width: 15, flexShrink: 0 }} />
                </Tooltip>
            );
        }

        return (
            <Tooltip title={status.message} arrow>
                <Avatar sx={{ bgcolor: status.color, height: 15, width: 15, flexShrink: 0 }}> </Avatar>
            </Tooltip>
        );
    };

    const renderHostedStatusText = (status) => (
        <Box component="span" sx={{ fontWeight: 'bold', color: status.textColor, minWidth: 0 }}>
            {status.message}
            {status.level >= 2 && (
                <>
                    {" "}
                    <Link href={`mailto:${SUPPORT_EMAIL_TO_RECEIVE_SUPPORT_REQUESTS}`}>Contact Us</Link>
                </>
            )}
        </Box>
    );

    const renderHostedNotProvisioned = () => (
        <Box sx={containerSx}>
            {viewFullSize ? renderTenantHeading({ fullSize: true }) : renderTenantHeading()}
            <br /><br />
            Not yet provisioned or setup.
        </Box>
    );

    const renderHostedTenantStats = () => {
        // For Audit Vault hosted databases, bill/alert against true used space.
        // SQL Server keeps deleted space inside the database files for reuse, so
        // subtract reported reusable free space from the physical file sizes.
        const totalUnitsOfStorageInGB = tenantDbStats.dbUnitsPurchased * AUDIT_VAULT_TENANT_DBUNITSPURCHASED_GB_PER_UNIT;

        if (totalUnitsOfStorageInGB === 0) {
            return renderHostedNotProvisioned();
        }

        const dbUsedSpaceInGB = Math.max((tenantDbStats.dbCurrentSizeInGB || 0) - (tenantDbStats.dbReportedFreeSpaceInGB || 0), 0);
        const logUsedSpaceInGB = Math.max((tenantDbStats.logCurrentSizeInGB || 0) - (tenantDbStats.logReportedFreeSpaceInGB || 0), 0);
        const currentUsedStorageInGB = dbUsedSpaceInGB + logUsedSpaceInGB;
        const reusableFreeSpaceInGB = (tenantDbStats.dbReportedFreeSpaceInGB || 0) + (tenantDbStats.logReportedFreeSpaceInGB || 0);
        const currentOverallUsagePercentNumber = (currentUsedStorageInGB / totalUnitsOfStorageInGB) * 100;
        const currentOverallUsagePercent = currentOverallUsagePercentNumber.toFixed(1);
        const remainingStorageInGB = (totalUnitsOfStorageInGB - currentUsedStorageInGB).toFixed(3);
        const status = getHostedStorageStatus(currentOverallUsagePercentNumber);
        const displayText = `${currentOverallUsagePercent}% (${currentUsedStorageInGB.toFixed(2)} of ${totalUnitsOfStorageInGB} GB)`;

        if (!viewFullSize) {
            return (
                <Box sx={containerSx}>
                    {renderTenantHeading()}
                    <br /><u>Storage Usage:</u>
                    <Stack spacing={0.75} sx={detailsSx}>
                        <Box component="li" sx={statusRowSx}>
                            {renderHostedStatusIcon(status, "circle")}
                            <Box component="span">{displayText}</Box>
                        </Box>
                        <LinearProgress variant="determinate" value={currentOverallUsagePercentNumber} sx={progressSx} />
                    </Stack>
                    {renderJobHealth()}
                </Box>
            );
        }

        return (
            <Box sx={containerSx}>
                <Box sx={sectionHeaderSx}>
                    <Tooltip title={`Your storage usage indicates the cumulative amount of data and logs being managed by Audit Vault for M365 for this specific tenant (${tenant.tenantUrl}).`} arrow>
                        <StorageIcon color="action" />
                    </Tooltip>
                    <span>Storage Usage Info</span>
                </Box>
                <Stack spacing={0.75} sx={detailsSx}>
                    <Box />
                    You've used {currentOverallUsagePercent}% of your storage<br />
                    <LinearProgress variant="determinate" value={currentOverallUsagePercentNumber} sx={progressSx} />
                    <Box>{remainingStorageInGB} GB remaining available of {totalUnitsOfStorageInGB} GB</Box>
                </Stack>
                <Stack spacing={0} sx={detailsSx}>
                    <Box>Used Storage: {currentUsedStorageInGB.toFixed(2)} GB</Box>
                    <Box>Allocated File Size: {tenantDbStats.currentOverallUsageInGB.toFixed(2)} GB</Box>
                    <Box>Reusable Free Space: {reusableFreeSpaceInGB.toFixed(2)} GB</Box>
                    <Box>Your current plan's storage entitlement is {totalUnitsOfStorageInGB} GB</Box>
                </Stack>
                <Stack spacing={0.75} sx={detailsSx}>
                    <Box component="li" sx={statusRowSx}>
                        <b>Current Status:</b>
                        {renderHostedStatusIcon(status)}
                        {renderHostedStatusText(status)}
                    </Box>
                </Stack>
                {renderReportsButton()}
            </Box>
        );
    };

    const getUsagePercentage = (currentSize, reportedFreeSpace) => {
        const totalSpace = (currentSize || 0) + (reportedFreeSpace || 0);
        return totalSpace > 0 ? ((currentSize || 0) / totalSpace) * 100 : 0;
    };

    const renderSelfHostedStatBlock = ({
        icon,
        title,
        tooltip,
        usagePercentage,
        currentSize,
        reportedFreeSpace,
        maxSize,
    }) => (
        <Box sx={{ ...detailsSx, flex: 1, minWidth: 0 }}>
            <Box sx={sectionHeaderSx}>
                <Tooltip title={tooltip} arrow>
                    {icon}
                </Tooltip>
                <span>{title}</span>
            </Box>
            You've used {usagePercentage.toFixed(2)}% of your {title.toLowerCase()} storage<br />
            <LinearProgress variant="determinate" value={usagePercentage} sx={progressSx} />
            Current size: {currentSize.toFixed(2)} GB<br />
            Reported free space: {reportedFreeSpace.toFixed(2)} GB<br />
            Reported max size: {tenantDbStats.maxSizeIsUnlimitied ? (<>Unlimited</>) : maxSize.toFixed(2)} GB<br />
        </Box>
    );

    const renderSelfHostedTenantStats = () => {
        if (!viewFullSize) {
            return (
                <Box sx={containerSx}>
                    {renderTenantHeading()}
                    <br /><u>Storage Usage:</u><br />
                    Database is self hosted.<br />                    
                    {renderJobHealth()}
                </Box>
            );
        }

        const dbUsagePercentage = getUsagePercentage(tenantDbStats.dbCurrentSizeInGB, tenantDbStats.dbReportedFreeSpaceInGB);
        const logUsagePercentage = getUsagePercentage(tenantDbStats.logCurrentSizeInGB, tenantDbStats.logReportedFreeSpaceInGB);

        return (
            <Box sx={containerSx}>
                Your Database is self hosted. Please ensure you proactively maintain, monitor and backup your data.<br /><br />
                Storage Usage Info<br />
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    sx={{ width: '100%', mt: 1 }}
                >
                    {renderSelfHostedStatBlock({
                        icon: <StorageIcon color="action" />,
                        title: "Database",
                        tooltip: "The Audit Vault Control SQL Database: Data in the SQL Server database system, used for storing and retrieving data efficiently.",
                        usagePercentage: dbUsagePercentage,
                        currentSize: tenantDbStats.dbCurrentSizeInGB,
                        reportedFreeSpace: tenantDbStats.dbReportedFreeSpaceInGB,
                        maxSize: tenantDbStats.dbReportedMaxSizeInGB,
                    })}
                    {renderSelfHostedStatBlock({
                        icon: <Inventory2Icon color="action" />,
                        title: "Transaction Log",
                        tooltip: "The Audit Vault Control SQL Transaction Log: A file that records all modifications made to a SQL Server database, ensuring transactional consistency, durability, and providing the ability to recover the database to a specific point in time.",
                        usagePercentage: logUsagePercentage,
                        currentSize: tenantDbStats.logCurrentSizeInGB,
                        reportedFreeSpace: tenantDbStats.logReportedFreeSpaceInGB,
                        maxSize: tenantDbStats.logReportedMaxSizeInGB,
                    })}
                </Stack>
                {renderReportsButton()}
            </Box>
        );
    };

    return tenant.isDatabaseHostedByCompany
        ? renderSelfHostedTenantStats()
        : renderHostedTenantStats();
}
export default TenantDBStats;
